import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useChartStore } from '@/store/chartStore';
import { useUiStore } from '@/store/uiStore';
import { trackNudgeShown } from '@/services/gtag';

export type NudgeType = 'signin' | 'pro' | 'alerts' | 'screening' | 'financials';

const OPT_OUT_KEY = 'cseview-nudge-opted-out';
const COOLDOWN_PREFIX = 'cseview-nudge-last-shown-';
const NUDGE_MIN_DELAY_MS = 45 * 1000;
const NUDGE_MAX_DELAY_MS = 90 * 1000;

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function cooldownKey(type: NudgeType): string {
  return COOLDOWN_PREFIX + type;
}

function shownToday(type: NudgeType): boolean {
  return window.localStorage.getItem(cooldownKey(type)) === todayKey();
}

function showNudge(type: NudgeType, setNudgeType: (t: NudgeType) => void) {
  window.localStorage.setItem(cooldownKey(type), todayKey());
  trackNudgeShown(type);
  setNudgeType(type);
}

export function useNudgeTimer() {
  const loading  = useAuthStore((s) => s.loading);
  const user     = useAuthStore((s) => s.user);
  const drawingTaskActive = useChartStore((s) => s.drawingTaskActive);
  const openModalCount    = useUiStore((s) => s.openModalCount);
  const [nudgeType, setNudgeType] = useState<NudgeType | null>(null);

  // A nudge that was picked and delay-elapsed while the user was mid-task
  // (e.g. drawing on the chart, or any other modal/dialog is open — login,
  // upgrade, settings, screener, etc.) — held here until that task finishes
  // / everything closes, instead of popping up over it.
  const queuedRef = useRef<NudgeType | null>(null);

  const plan            = user?.plan;
  const signedIn         = !!user;
  const unverifiedGate  = !!user && !user.emailVerified && !user.pendingEmail;
  const isProTier       = plan === 'pro' || plan === 'business';

  useEffect(() => {
    if (loading || unverifiedGate || nudgeType || queuedRef.current) return;

    let pool: NudgeType[];
    if (!signedIn) {
      pool = ['signin'];
    } else if (plan === 'basic') {
      pool = ['pro', 'alerts', 'screening', 'financials'];
    } else if (isProTier) {
      if (window.localStorage.getItem(OPT_OUT_KEY) === '1') return;
      pool = ['alerts', 'screening', 'financials'];
    } else {
      return;
    }

    const eligible = pool.filter((t) => !shownToday(t));
    if (eligible.length === 0) return;

    const next = eligible[Math.floor(Math.random() * eligible.length)];
    const delay = NUDGE_MIN_DELAY_MS + Math.random() * (NUDGE_MAX_DELAY_MS - NUDGE_MIN_DELAY_MS);
    const timeoutId = window.setTimeout(() => {
      if (useChartStore.getState().drawingTaskActive || useUiStore.getState().openModalCount > 0) {
        // Hold it — the release effect below fires it once things clear up.
        queuedRef.current = next;
      } else {
        showNudge(next, setNudgeType);
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [loading, unverifiedGate, nudgeType, signedIn, plan, isProTier]);

  // Release effect: flush a queued nudge once drawing ends and no other
  // modal/dialog is open.
  useEffect(() => {
    if (!drawingTaskActive && openModalCount === 0 && queuedRef.current) {
      const type = queuedRef.current;
      queuedRef.current = null;
      showNudge(type, setNudgeType);
    }
  }, [drawingTaskActive, openModalCount]);

  function dismiss() {
    setNudgeType(null);
  }

  function optOut() {
    window.localStorage.setItem(OPT_OUT_KEY, '1');
  }

  return { nudgeType, dismiss, optOut };
}
